import {
  AutoComplete,
  Button,
  Form,
  message,
  Modal,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { useReportTokenGenerate } from '@/core/domain/reports/reportToken/reportToken.hooks';
import { useWorkorderChecklistContext } from '../../WorkorderChecklistProvider';
import { ReportTokenType } from '@/core/domain/reports/reportToken/reportToken.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import { FloatingFormItem } from '@/components/_common';
import { removeMask, applyMask } from '@/utils/mask';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import flatContacts from '@/utils/functions/flatContacts';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { useState } from 'react';
import notificationError from '@/utils/functions/notificationError';
import { WorkorderChecklistItemType } from '../../WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { useDiagnosticDetail } from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';

interface Props {
  disabled: boolean;
  tooltipMessage?: string;
}

export default function ChecklistWhatsAppButton(props: Props) {
  const reportTokenGenerate = useReportTokenGenerate();

  const [form] = Form.useForm();
  const phoneNumber = Form.useWatch(['number'], form);
  const isValidPhoneNumber = !!phoneNumber && (phoneNumber?.length || 0) >= 10;

  const { isMobile } = useWindowSize();

  const [opened, action] = useDisclosure();

  const [loading, setLoading] = useState(false);

  const { checklistItemId, workorderId, diagnosticId, checklistItemType } =
    useWorkorderChecklistContext();

  const { data: workOrder } = useWorkOrder(workorderId);
  const { data: diagnostic } = useDiagnosticDetail(diagnosticId);

  const customerId =
    checklistItemType === WorkorderChecklistItemType.Workorder
      ? workOrder?.customer?.id
      : diagnostic?.customer.id;

  const { data: contactsData } = useCustomerContacts(
    customerId,
    opened && !!customerId,
  );

  const contacts = flatContacts(contactsData).filter(
    (contact) =>
      contact.contactType === ContactType.Phone ||
      contact.contactType === ContactType.Mobile ||
      contact.contactType === ContactType.WhatsApp,
  );

  async function sendMessage(number: string) {
    setLoading(true);

    try {
      const checklistToken = await reportTokenGenerate.mutateAsync({
        params: JSON.stringify({
          ...(checklistItemType === WorkorderChecklistItemType.Diagnostic
            ? { diagnosticId: checklistItemId }
            : { workOrderComplementId: checklistItemId }),
        }),
        reportType: ReportTokenType.CHECKLIST,
      });

      if (!checklistToken.token) {
        message.destroy();
        message.warn(
          'Não foi possível gerar o token de compartilhamento, tente novamente',
        );
      }

      const checklistUrl = `${window.location.origin}/r/c/checklist?token=${checklistToken.token}`;

      const wppText = `Olá! segue o link do checklist do veículo\n\n${checklistUrl}`;

      const wppNumber = `55${number}`;

      const urlQueryString = new URLSearchParams({
        phone: wppNumber,
        text: wppText,
      });

      if (isMobile) {
        window.open(
          `https://wa.me/${wppNumber}?text=${encodeURIComponent(wppText)}`,
          '_blank',
        );
      } else {
        window.open(
          `https://web.whatsapp.com/send?${urlQueryString}`,
          '_blank',
        );
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit() {
    try {
      const values = await form.validateFields();

      const number = removeMask(values.number);

      sendMessage(number);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formError = error as any;

      if (formError?.values && !formError?.values?.number) {
        return message.error('Informe o número do cliente');
      } else {
        return notificationError(error);
      }
    }
  }

  const handleClose = () => {
    form.resetFields();
    action.close();
  };

  return (
    <>
      <Tooltip
        title={
          props.disabled && (props.tooltipMessage || 'Não há veículo vinculado')
        }
      >
        <Button
          type="primary"
          onClick={action.open}
          disabled={props.disabled}
          block
        >
          Enviar por WhatsApp
        </Button>
      </Tooltip>

      <Modal
        open={opened}
        onCancel={handleClose}
        title="Enviar checklist por WhatsApp"
        confirmLoading={loading}
        onOk={onSubmit}
        okText="Enviar checklist"
        okButtonProps={{
          disabled: !isValidPhoneNumber,
        }}
      >
        <Form form={form} preserve={false}>
          <Space direction="vertical">
            <Typography.Text>
              Para enviar o link do checklist do veículo, preencha o número do
              WhatsApp do cliente.
            </Typography.Text>

            <FloatingFormItem
              name="number"
              label="Número do cliente"
              getValueFromEvent={(value) => {
                if (value.length < 11) {
                  return removeMask(value);
                }
                return applyMask(removeMask(value), cellphoneMask);
              }}
            >
              <AutoComplete
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                options={contacts.map((contact) => ({
                  value: contact.value,
                  label: applyMask(removeMask(contact.value), cellphoneMask),
                }))}
              />
            </FloatingFormItem>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
